import FormattedMessage from 'I18n/components/FormattedMessage';
import { BillingWizardHost, CreateSubscriptionTooltip, AssociationBuilder } from 'commerce-subscription-lib/public/billingWizard';
import { ConvertToSubscriptionValidationTooltip } from 'commerce-subscription-lib/public/convertToSubscription';
import { Button } from 'foundations-components/transitional/components/Button';
import { List } from 'foundations-components/transitional/components/List';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { staleDataNotifier } from 'commerce-subscription-lib/public/utilities';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { COMMERCE_PAYMENT_TYPE_ID, COMPANY_TYPE_ID, CONTACT_TYPE_ID, CONTRACT_TYPE_ID, DEAL_TYPE_ID, INVOICE_TYPE_ID, PAYMENT_LINK_TYPE_ID, QUOTE_TYPE_ID, TICKET_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { useCallback, useState } from 'react';
import { useFetchContractBillingEnabled } from './hooks/useFetchContractBillingEnabled';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function ActionButtons({
  fromObjectId,
  fromObjectTypeId,
  canEditAssociation,
  trackEvent,
  openWizard,
  toObjectTypeId,
  associationTypeData
}) {
  const modifyAssociations = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_OBJECT_BUILDER);
  const openAssociationPanel = useCallback(() => {
    return modifyAssociations({
      objectTypeId: toObjectTypeId,
      association: {
        objectTypeId: fromObjectTypeId,
        objectId: fromObjectId,
        associationTypeId: associationTypeData.associationTypeId,
        associationCategory: associationTypeData.associationCategory
      }
    });
  }, [modifyAssociations, toObjectTypeId, fromObjectTypeId, fromObjectId, associationTypeData.associationTypeId, associationTypeData.associationCategory]);
  return /*#__PURE__*/_jsxs(List, {
    wrapChildren: true,
    children: [fromObjectTypeId === DEAL_TYPE_ID && /*#__PURE__*/_jsx(ConvertToSubscriptionValidationTooltip, {
      objectId: fromObjectId,
      objectTypeId: fromObjectTypeId,
      renderButton: ({
        loading,
        disabled
      }) => {
        return /*#__PURE__*/_jsx(Button, {
          "data-test-id": "commerce-subscription-association-card:convert-button",
          disabled: loading || disabled,
          onClick: () => {
            trackEvent === null || trackEvent === void 0 || trackEvent(`click convert from ${fromObjectTypeId}`);
            openWizard(true);
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "association.buttonLabels.convert"
          })
        });
      }
    }), /*#__PURE__*/_jsx(CreateSubscriptionTooltip, {
      renderChild: ({
        disabled,
        loading
      }) => {
        return /*#__PURE__*/_jsx(Button, {
          "data-test-id": "commerce-subscription-association-card:create-button",
          disabled: loading || disabled,
          onClick: () => {
            trackEvent === null || trackEvent === void 0 || trackEvent(`click create new subscription from ${fromObjectTypeId}`);
            openWizard(false);
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "association.buttonLabels.createNew"
          })
        });
      }
    }), /*#__PURE__*/_jsx(Tooltip, {
      placement: "left",
      disabled: canEditAssociation,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "association.tooltip.missingPermission.createAssociation"
      }),
      children: /*#__PURE__*/_jsx(Button, {
        "data-test-id": "commerce-subscription-association-card:association-button",
        disabled: !canEditAssociation,
        onClick: () => {
          trackEvent === null || trackEvent === void 0 || trackEvent(`click add existing subscription to ${fromObjectTypeId}`);
          openAssociationPanel();
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "association.buttonLabels.addExisting"
        })
      })
    })]
  });
}
export function CardAction(props) {
  const {
    fromObjectId,
    fromObjectTypeId,
    trackEvent,
    onClose,
    toObjectTypeId,
    canEditAssociation,
    canCreateAssociation
  } = props;
  const [open, setOpen] = useState(false);
  const updateAssociationCardData = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_ASSOCIATIONS);
  const {
    isContractBillingEnabled
  } = useFetchContractBillingEnabled(fromObjectId.toString());
  const refetchCardData = useCallback(() => updateAssociationCardData({
    objectTypeId: fromObjectTypeId,
    objectId: fromObjectId.toString(),
    toObjectTypeId
  }), [fromObjectTypeId, fromObjectId, toObjectTypeId, updateAssociationCardData]);
  const [billingWizardProps, setBillingWizardProps] = useState({
    initializeArgs: {
      fromObjectId,
      fromObjectTypeId
    },
    isOpen: false
  });
  const handleOpen = useCallback(isConverting => {
    const builder = new AssociationBuilder();
    switch (fromObjectTypeId) {
      case TICKET_TYPE_ID:
        builder.withTicketId(fromObjectId);
        break;
      case CONTRACT_TYPE_ID:
        builder.withContractId(fromObjectId);
        break;
      case PAYMENT_LINK_TYPE_ID:
        builder.withPaymentLinkId(fromObjectId);
        break;
      case COMMERCE_PAYMENT_TYPE_ID:
        builder.withCommercePaymentId(fromObjectId);
        break;
      case INVOICE_TYPE_ID:
        builder.withInvoiceId(fromObjectId);
        break;
      case QUOTE_TYPE_ID:
        builder.withQuoteId(fromObjectId);
        break;
      case CONTACT_TYPE_ID:
        builder.withContactId(fromObjectId);
        break;
      case COMPANY_TYPE_ID:
        builder.withCompanyId(fromObjectId);
        break;
      case DEAL_TYPE_ID:
        builder.withDealId(fromObjectId);
        break;
      default:
        break;
    }
    setBillingWizardProps({
      initializeArgs: {
        fromObjectId: isConverting ? fromObjectId : undefined,
        fromObjectTypeId: isConverting ? fromObjectTypeId : undefined,
        associations: builder.build()
      },
      isOpen: true
    });
  }, [fromObjectId, fromObjectTypeId]);
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "SubscriptionCardAction-DefaultAction",
    children: [/*#__PURE__*/_jsx(BillingWizardHost, Object.assign({
      id: "subscription-association-card:billing-wizard-host",
      "data-test-id": "subscription-association-card:billing-wizard-host",
      onClose: payload => {
        var _payload$createdObjec;
        setBillingWizardProps(p => Object.assign({}, p, {
          isOpen: false
        }));
        if ('error' in payload) {
          return;
        }
        staleDataNotifier.broadcast({
          objects: (_payload$createdObjec = payload === null || payload === void 0 ? void 0 : payload.createdObjects) !== null && _payload$createdObjec !== void 0 ? _payload$createdObjec : []
        });
        onClose === null || onClose === void 0 || onClose();
      }
    }, billingWizardProps)), /*#__PURE__*/_jsx(Tooltip, {
      disabled: fromObjectTypeId !== CONTRACT_TYPE_ID || !isContractBillingEnabled,
      placement: "left",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "association.tooltip.contractBillingEnabled"
      }),
      children: /*#__PURE__*/_jsx(Dropdown, {
        disabled: fromObjectTypeId === CONTRACT_TYPE_ID && isContractBillingEnabled,
        buttonUse: "link",
        buttonSize: "small",
        buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "association.buttonLabels.add"
        }),
        open: open,
        onOpenChange: e => {
          setOpen(e.target.value);
          if (e.target.value) {
            trackEvent === null || trackEvent === void 0 || trackEvent('click subscription add dropdown');
          }
        },
        children: /*#__PURE__*/_jsx(ActionButtons, Object.assign({}, props, {
          onClose: refetchCardData,
          openWizard: handleOpen,
          canCreateAssociation: canCreateAssociation !== null && canCreateAssociation !== void 0 ? canCreateAssociation : canEditAssociation
        }))
      })
    })]
  });
}